import { useQuery, useMutation } from '@tanstack/react-query';
import { api as backendApi } from '../api/client';

export interface SubscriptionLimits {
    maxExpenses: number;
    maxStorageBytes: number;
    expensesUsed: number;
    storageBytesUsed: number;
}

export interface SubscriptionStatus {
    tier: string;
    planName: string;
    isFoundingMember: boolean;
    isLifetime: boolean;
    status: string;
    expiresAt: string | null;
    features: Record<string, boolean>;
    limits: SubscriptionLimits;
}

export interface Plan {
    id: string;
    name: string;
    tier: string;
    price: number;
    billingInterval: string;
    isDefault: boolean;
    isFoundingPlan: boolean;
    maxExpenses: number;
    maxStorageBytes: number;
    maxBulkImports: number;
    maxHsaAccounts: number;
    features: string[];
}

export function useSubscription() {
    const statusQuery = useQuery({
        queryKey: ['subscription', 'status'],
        queryFn: async () => {
            const res = await backendApi.getSubscriptionStatus();
            if (!res) throw new Error("Failed to fetch subscription status");
            return res;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    const plansQuery = useQuery({
        queryKey: ['subscription', 'plans'],
        queryFn: async () => {
            const res = await backendApi.getSubscriptionPlans();
            if (!res) throw new Error("Failed to fetch subscription plans");
            return res;
        },
        staleTime: 60 * 60 * 1000, // 1 hour
    });

    const createCheckoutSession = useMutation({
        mutationFn: async (planId: string) => {
            const res = await backendApi.createCheckoutSession(planId);
            if (!res) throw new Error("Failed to create checkout session");
            return res.url;
        },
    });

    const createCustomerPortalSession = useMutation({
        mutationFn: async () => {
            const res = await backendApi.createCustomerPortalSession();
            if (!res) throw new Error("Failed to create customer portal session");
            return res.url;
        },
    });

    return {
        status: statusQuery.data,
        isLoading: statusQuery.isLoading,
        error: statusQuery.error,
        refetchStatus: statusQuery.refetch,

        plans: plansQuery.data,
        isLoadingPlans: plansQuery.isLoading,

        createCheckoutSession,
        createCustomerPortalSession,

        // Helper functions
        hasFeature: (featureKey: string) => statusQuery.data?.features[featureKey] ?? false,

        expenseLimitReached: () => {
            if (!statusQuery.data) return false;
            const { expensesUsed, maxExpenses } = statusQuery.data.limits;
            return maxExpenses > 0 && expensesUsed >= maxExpenses;
        },

        storageLimitReached: () => {
            if (!statusQuery.data) return false;
            const { storageBytesUsed, maxStorageBytes } = statusQuery.data.limits;
            return maxStorageBytes > 0 && storageBytesUsed >= maxStorageBytes;
        },
    };
}
